import {
  BookmarkIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/solid"
import clsx from "clsx"
import React from "react"
import { SubmitHandler, useForm } from "react-hook-form"
import ButtonWithIcon from "../ui/button/ButtonWithIcon"
import BasicValidationMessage from "../ui/validation/BasicValidationMessage"

export type TempVideoUpload = {
  video
}

const CourseVideoManagementFormTempUpload: React.FC<{
  method: "POST" | "PUT"
  onSubmitHandler: SubmitHandler<TempVideoUpload>
  submitting?: boolean
  uploadProgress?: number
}> = (props) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    watch,
  } = useForm<TempVideoUpload>({
    mode: "all",
  })
  watch("video")

  return (
    <form
      method={props.method}
      onSubmit={handleSubmit(props.onSubmitHandler)}
    >
      <div className="grid gap-4 sm:grid-cols-2 sm:gap-6">
        <div className="sm:col-span-2">
          <div
            className={clsx(
              "border border-dashed border-white relative max-w-[42rem]",
              errors.video && "mb-2"
            )}
          >
            <input
              type="file"
              className="cursor-pointer relative block opacity-0 w-full h-full p-20 z-50"
              {...register("video", {
                validate: {
                  lessThanAndEqual300MB: (files) =>
                    files[0]?.size <= 300_000_000 ||
                    "Video tidak boleh melebihi 300MB",
                  acceptedFormats: (files) =>
                    ["video/mp4"].includes(files[0]?.type) ||
                    "Hanya dapat mengunggah video dengan format mp4",
                },
                required: "Video tidak boleh kosong!",
              })}
              accept="video/mp4"
            />
            <div className="flex flex-col space-y-1 p-10 absolute top-0 right-0 left-0 m-auto">
              <div className="text-center text-white">
                <h4>
                  Drop files anywhere to upload
                  <br />
                  or
                </h4>
                <p className="">Select Files</p>
                <span className="text-xs text-gray-400">300MB Max</span>
              </div>
              {getValues("video")?.length > 0 && (
                <h4 className="text-center text-primary">
                  {getValues("video")[0].name}
                </h4>
              )}
            </div>
          </div>
          {errors.video && (
            <BasicValidationMessage
              type="error"
              message={errors.video.message as string}
              icon={
                <ExclamationTriangleIcon className="h-6 w-6 text-red-800" />
              }
            />
          )}
        </div>
        {props.submitting && (
          <div className="sm:col-span-2 max-w-[42rem]">
            <div className="flex justify-between mb-1">
              <span className="text-base font-medium text-white">
                Proses Upload
              </span>
              <span className="text-sm font-medium text-white">
                {props.uploadProgress}%
              </span>
            </div>
            <div className="w-full rounded-full h-2.5 bg-gray-700">
              <div
                className="bg-blue-600 h-2.5 rounded-full"
                style={{ width: `${props.uploadProgress}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>
      {!props.submitting && (
        <ButtonWithIcon
          action={undefined}
          type="submit"
          class="mt-4 border-2 border-transparent bg-primary text-white space-x-2 hover:bg-transparent hover:text-white hover:border-primary"
        >
          <BookmarkIcon className="h-6 w-6 text-white" />
          <span>Unggah</span>
        </ButtonWithIcon>
      )}
      {props.submitting && (
        <ButtonWithIcon
          action={undefined}
          type="button"
          class="mt-4 border-2 border-transparent bg-primary text-white space-x-2 cursor-not-allowed"
          disabled
        >
          <svg
            aria-hidden="true"
            className="inline w-6 h-6 mr-2 text-gray-400 animate-spin fill-white"
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill"
            />
          </svg>
          <span>Mengunggah...</span>
        </ButtonWithIcon>
      )}
    </form>
  )
}

export default CourseVideoManagementFormTempUpload
